import { isClearingBankPayment } from '@/lib/payments/savedPaymentMethods';
import type { EmailDeliverySummary } from '@/types/email-delivery';
import { useApiQuery } from '../useApiQuery';
import { apiGet, operationsClient } from '@/lib/api/client';
import { paymentKeys } from '@/lib/queryKeys';

export interface PaymentRecord {
  id: string;
  paymentNumber?: string;
  tenantId?: string;
  createdBy?: string | null;
  updatedBy?: string | null;
  invoiceId: string;
  invoiceNumber?: string | null;
  invoiceStatus?: string | null;
  invoiceCustomerName?: string | null;
  jobId: string | null;
  contactId?: string | null;
  estimateId?: string | null;
  estimateNumber?: string | null;
  amount?: number;
  refundedAmount?: number | null;
  paymentMethod?: string;
  /** User-entered reference (e.g. a check number). Returned by the detail endpoint. */
  reference?: string | null;
  /**
   * Set on the negative offsetting row a bank return (ACH return / bounced
   * check) writes: the id of the payment the bank took back.
   */
  reversalOf?: string | null;
  /** The returned payment's PAY- number, for display next to `reversalOf`. */
  reversalOfNumber?: string | null;
  /**
   * Whether this payment can be marked returned by the bank, decided by the
   * server (processor payments, offsets and refunded payments are refused).
   * `kind` is null when the payment is not a returnable kind at all. Detail
   * endpoint only.
   */
  reversal?: {
    eligible: boolean;
    kind: 'ach_return' | 'check_bounce' | null;
    blockedReason: string | null;
  };
  status: string;
  transactionId: string | null;
  processedAt: string | null;
  createdAt: string;
  updatedAt?: string;
  failureReason?: string | null;
  refundReason: string | null;
  refundedAt: string | null;
  qbPaymentId: string | null;
  qbSyncedAt?: string | null;
  gatewayProvider?: string | null;
  gatewayTxnId?: string | null;
  idempotencyKey?: string | null;
  /** How the payment came in; `CHEQUE_SCAN` for a check scanned through Payabli. */
  flow?: string | null;
  currency?: string | null;
  /**
   * Card service fee charged ON TOP of `amount` (a surcharge, not the
   * gateway's cut). `amount` stays the NET the invoice was credited, so the
   * money that actually left the customer's card is `amount + surchargeAmount`.
   * Serialized from a Prisma Decimal, so it can arrive as a string.
   */
  surchargeAmount?: number | string | null;
  processingFeeCents?: number | null;
  netAmountCents?: number | null;
  receiptSentAt?: string | null;
  receiptDeliveryMethod?: string | null;
  // The receipt generated for this payment (1:1). Null until/unless a receipt
  // exists (e.g. failed payments never get one).
  receiptId?: string | null;
  receiptNumber?: string | null;
  refundableBalance?: number;
  creditIssuableBalance?: number;
  /**
   * Customer credit issued from this payment (not voided). Detail endpoint
   * only. It is not a refund, but it no longer counts as refundable.
   */
  issuedCredits?: Array<{
    id: string;
    creditMemoNumber: string;
    amount: number;
    issuedAt: string;
  }>;
  gatewayMetadata?: Record<string, unknown> | null;
  accountingSync?: Record<string, unknown> | null;
  disputedAt?: string | null;
  disputeResolvedAt?: string | null;
  source: string;
  applications?: Array<{
    id: string;
    invoiceId: string;
    invoiceNumber?: string | null;
    invoiceStatus?: string | null;
    amount: number;
    status: string;
    reversedAt?: string | null;
  }>;
  /**
   * The customer's copy of a staff-taken bank-debit authorization, on the
   * detail read only. A bank payment still clearing has no receipt, so this is
   * where staff see whether it went out.
   */
  achAuthorizationCopy?: EmailDeliverySummary | null;
}

export interface PaymentsResponse {
  data: PaymentRecord[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasMore: boolean;
  /**
   * The company timezone the server read the date filter in. Rows are dated in
   * it so the day shown matches the day filtered, for every role — including
   * one that cannot read the company settings.
   */
  timeZone?: string;
}

export interface PaymentsParams {
  enabled?: boolean;
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  paymentMethod?: string;
  transactionId?: string;
  amountOp?: 'eq' | 'gt' | 'lt' | 'gte' | 'lte';
  amountValue?: number;
  syncStatus?: 'SYNCED' | 'NOT_SYNCED';
  dateFrom?: string;
  dateTo?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export function usePayments(params: PaymentsParams = {}) {
  return useApiQuery<PaymentsResponse>({
    queryKey: paymentKeys.list(params),
    enabled: params.enabled ?? true,
    queryFn: () =>
      apiGet<PaymentsResponse>(operationsClient, '/payments', {
        params: {
          ...(params.page && { page: params.page }),
          ...(params.limit && { limit: params.limit }),
          ...(params.search && { search: params.search }),
          ...(params.status && { status: params.status }),
          ...(params.paymentMethod && { paymentMethod: params.paymentMethod }),
          ...(params.transactionId && { transactionId: params.transactionId }),
          ...(params.amountOp && { amountOp: params.amountOp }),
          ...(typeof params.amountValue === 'number' && { amountValue: params.amountValue }),
          ...(params.syncStatus && { syncStatus: params.syncStatus }),
          ...(params.dateFrom && { dateFrom: params.dateFrom }),
          ...(params.dateTo && { dateTo: params.dateTo }),
          ...(params.sortBy && { sortBy: params.sortBy }),
          ...(params.sortDir && { sortDir: params.sortDir }),
        },
      }),
    refetchInterval: (query) =>
      query.state.data?.data.some(isClearingBankPayment) ? 30_000 : false,
    showErrorToast: false,
  });
}
